'use client';

import { useEffect } from 'react';
import { buttonClasses, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { Button, Stack, Tooltip } from '@/primitives';
import { useAnimalDisplayType } from '@/hooks/publicSearch/animal/useAnimalDisplayType';
import { useResponsive } from '@/hooks/publicSearch/common/useResponsive';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import { RESULT_DISPLAY } from '@/configs/storageKeys';
import { RESULT_DISPLAY_TYPE } from '@/constants/resultDisplay';
const listOnlySearchTypes: string[] = [];

const ResultToolbarButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  border: '0.5px solid',
  borderColor: (theme.vars ?? theme).palette.primary.main,
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: (theme.vars ?? theme).palette.action.hover,
  },
  [`&.${buttonClasses.disabled}`]: {
    border: '1px solid',
    borderColor: (theme.vars ?? theme).palette.primary.main,
  },
}));

export default function SearchDisplayTypeSelection() {
  const { resultDisplay, setResultDisplay } = useAnimalDisplayType();
  const { isMobile } = useResponsive();
  const searchType = useSearchType();

  useEffect(() => {
    const lastSavedResultDisplay = sessionStorage.getItem(RESULT_DISPLAY) as
      | typeof resultDisplay
      | null;
    const savedResultDisplay =
      lastSavedResultDisplay ??
      (isMobile ? RESULT_DISPLAY_TYPE.GALLERY : RESULT_DISPLAY_TYPE.LIST);
    setResultDisplay(savedResultDisplay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setResultDisplay]);

  useEffect(() => {
    if (
      resultDisplay !== RESULT_DISPLAY_TYPE.LIST &&
      listOnlySearchTypes.includes(searchType)
    ) {
      setResultDisplay(RESULT_DISPLAY_TYPE.LIST);
    }
  }, [resultDisplay, searchType, setResultDisplay]);

  const showGalleryOption = !listOnlySearchTypes.includes(searchType);
  const isListView = resultDisplay === RESULT_DISPLAY_TYPE.LIST;

  return (
    <Stack
      direction="row"
      sx={{
        flex: '0 0 auto',
        flexShrink: 0,
        height: { xs: '32px', md: '36px' },
      }}
    >
      {showGalleryOption && (
        <Tooltip title={isListView ? 'Card' : 'List'} placement="bottom">
          <span
            style={{
              display: 'inline-flex',
              height: '100%',
            }}
          >
            <ResultToolbarButton
              color="primary"
              sx={(theme) => ({
                margin: '0 !important',
                height: { xs: 32, md: 36 },
                width: { xs: 32, md: 36 },
                minWidth: { xs: 32, md: 36 },
                padding: '4px',
                '& svg': { fontSize: 18 },
                backgroundColor: isListView
                  ? 'transparent'
                  : alpha(theme.palette.primary.main, 0.1),
              })}
              variant="outlined"
              onClick={() =>
                setResultDisplay(
                  isListView
                    ? RESULT_DISPLAY_TYPE.GALLERY
                    : RESULT_DISPLAY_TYPE.LIST,
                )
              }
              data-dd-action-name={
                isListView ? 'View: Switch to Gallery' : 'View: Switch to List'
              }
              aria-label={
                isListView ? 'Switch to card view' : 'Switch to list view'
              }
            >
              {isListView ? <ViewModuleIcon /> : <FormatListBulletedIcon />}
            </ResultToolbarButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
}
